// Market Opportunity Signal — flag storefronts where an app is charting well
// but has very few reviews, suggesting an early-mover window for expansion.
//
// Rationale: a high chart rank with a low rating count means the app is
// visible in the store but users haven't reviewed it yet — indicating organic
// discovery without established competition. This is a useful signal for
// developers evaluating market expansion.
//
// Honesty note: this is a heuristic, not a guarantee of market potential.
// Chart rank reflects real-time Apple feed data; rating count is a proxy.

import { bestChartRank } from './chartRank.js';

/**
 * Default thresholds for the opportunity signal.
 * @type {{ chartRankThreshold: number, ratingCountThreshold: number }}
 */
export const DEFAULT_OPPORTUNITY_THRESHOLDS = Object.freeze({
  chartRankThreshold: 10,    // in top-N (inclusive)
  ratingCountThreshold: 50,  // fewer than N ratings
});

/**
 * @typedef {object} OpportunityResult
 * @property {boolean} isOpportunity
 * @property {number|null} chartRank - best chart rank for this row (free/paid/grossing min), or null.
 * @property {number|null} ratingCount - userRatingCount for this row, or null.
 */

/**
 * Determine whether a region row represents a market opportunity:
 * charting in top-N AND fewer than M ratings.
 *
 * @param {import('./normalizeApp.js').RegionRow & import('./chartRank.js').RowChart} row
 * @param {typeof DEFAULT_OPPORTUNITY_THRESHOLDS} [thresholds]
 * @returns {OpportunityResult}
 */
export function computeOpportunitySignal(row, thresholds = DEFAULT_OPPORTUNITY_THRESHOLDS) {
  if (!row || row.available === false) {
    return { isOpportunity: false, chartRank: null, ratingCount: null };
  }

  const chartRank = bestChartRank(row);
  const ratingCount = row.userRatingCount ?? null;

  const inTopN = chartRank !== null && chartRank <= thresholds.chartRankThreshold;
  const fewReviews = ratingCount !== null && ratingCount < thresholds.ratingCountThreshold;

  return {
    isOpportunity: inTopN && fewReviews,
    chartRank,
    ratingCount,
  };
}

/**
 * Annotate an array of region rows with opportunity signal.
 * Rows without chart enrichment (chartRankFree/Paid/Grossing all null/undefined)
 * will never be flagged as opportunities.
 *
 * @param {Array<import('./normalizeApp.js').RegionRow & import('./chartRank.js').RowChart>} rows
 * @param {typeof DEFAULT_OPPORTUNITY_THRESHOLDS} [thresholds]
 * @returns {Array<import('./normalizeApp.js').RegionRow & import('./chartRank.js').RowChart & { opportunity: OpportunityResult }>}
 */
export function annotateWithOpportunity(rows, thresholds = DEFAULT_OPPORTUNITY_THRESHOLDS) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => ({
    ...row,
    opportunity: computeOpportunitySignal(row, thresholds),
  }));
}
